import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import type { EmployeeNotaFiscalDto, EmployeeNotaFiscalListItem } from './types';

const MAX_MOTIVO = 500;

export interface NotaFiscalReprovarDialogProps {
  open: boolean;
  nota: EmployeeNotaFiscalListItem | null;
  useReprovar: () => UseMutationResult<
    EmployeeNotaFiscalDto,
    Error,
    { id: number; motivoReprovacao: string }
  >;
  onClose: () => void;
}

export function NotaFiscalReprovarDialog({
  open,
  nota,
  useReprovar,
  onClose,
}: NotaFiscalReprovarDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const reprovar = useReprovar();
  const [motivo, setMotivo] = useState('');
  const [touched, setTouched] = useState(false);

  function resetState(): void {
    setMotivo('');
    setTouched(false);
  }

  const motivoTrim = motivo.trim();
  const motivoError = touched && motivoTrim.length === 0;

  async function handleSubmit(): Promise<void> {
    if (!nota) return;
    setTouched(true);
    if (motivoTrim.length === 0) return;
    try {
      await reprovar.mutateAsync({ id: nota.id, motivoReprovacao: motivoTrim });
      toast.success(t('features.colaboradores.notasFiscais.toasts.reprovada'));
      onClose();
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: resetState }}
    >
      <DialogTitle>{t('features.colaboradores.notasFiscais.reprovar.title')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {nota && (
            <Typography variant="body2" color="text.secondary">
              {t('features.colaboradores.notasFiscais.reprovar.subtitle', {
                periodo: nota.periodo,
                cliente: nota.clienteNome,
              })}
            </Typography>
          )}
          <TextField
            label={t('features.colaboradores.notasFiscais.reprovar.motivoLabel')}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value.slice(0, MAX_MOTIVO))}
            onBlur={() => setTouched(true)}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            error={motivoError}
            helperText={
              motivoError
                ? t('features.colaboradores.notasFiscais.reprovar.motivoRequired')
                : t('features.colaboradores.notasFiscais.reprovar.motivoHelper', {
                    used: motivoTrim.length,
                    max: MAX_MOTIVO,
                  })
            }
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={reprovar.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => void handleSubmit()}
          disabled={reprovar.isPending}
        >
          {reprovar.isPending
            ? t('common.saving')
            : t('features.colaboradores.notasFiscais.reprovar.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
